import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { Subscription, forkJoin } from 'rxjs';
import * as XLSX from 'xlsx';
import { FormsModule } from '@angular/forms';

import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { DividerModule } from 'primeng/divider';
import { TabsModule } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { TimelineModule } from 'primeng/timeline';
import { TextareaModule } from 'primeng/textarea';
import { FieldsetModule } from 'primeng/fieldset';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { SelloutService } from '../sellout.service';
import { SelloutHeader } from '../sellout.model';
import { SelloutAttachment } from '../sellout-attachment.model';
import { SelloutDetail } from '../sellout-detail.model';
import { ChatSession } from '../../models/chat-session.model';
import { ChatSessionService } from '../../services/chat-session.service';
import { WorkflowApprovalService } from '../../chat-widgets/approval-card/workflow-approval.service';
import { ApprovalCardComponent } from '../../chat-widgets/approval-card/approval-card.component';
import { AppUser } from '../../users/user.model';
import { AuthorizationService } from '../../services/authorization.service';
import { WorkflowApproval } from '../../chat-widgets/approval-card/workflow-apporval.model';
import { SettlementService } from '../../settlement/settlement.service';

import { AppDatePipe } from '../../pipes/app-date.pipe';

@Component({
  selector: 'app-view-sellout',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ProgressSpinnerModule,
    ToastModule,
    TagModule,
    ButtonModule,
    FileUploadModule,
    DividerModule,
    FieldsetModule,
    TabsModule,
    TableModule,
    TooltipModule,
    DialogModule,
    TimelineModule,
    TextareaModule,
    NgxExtendedPdfViewerModule,
    ApprovalCardComponent,
    AppDatePipe
  ],
  providers: [MessageService],
  templateUrl: './view-sellout.component.html',
  styleUrl: './view-sellout.component.css'
})
export class ViewSelloutComponent implements OnInit, OnDestroy {
  private routeSub?: Subscription;
  private sessionSub?: Subscription;

  currentSessionId: number | null = null;
  currentSession?: ChatSession;
  currentUser: AppUser | null = null;

  selloutHeader: SelloutHeader | null = null;
  selloutId: number | null = null;
  isLoading = false;
  attachmentsChanged = false;

  canGenerateSettlement = false;
  canUploadAttachment = false;
  canOverrideHeader = false;
  overrideDialogVisible = false;
  overrideJustification = '';
  isApplyingOverride = false;

  pdfViewerVisible = false;
  pdfSrc: string | null = null;
  imageViewerVisible = false;
  officeViewerVisible = false;
  emlViewerVisible = false;
  imageSrc: string | null = null;
  officeSrc: SafeResourceUrl | null = null;
  emlSrc: SafeResourceUrl | null = null;

  currentTimelineStage = 'Not Started';
  timelineStages = [
    { status: 'Not Started', icon: 'pi pi-play' },
    { status: 'Under Execution', icon: 'pi pi-cog' },
    { status: 'Within 45 days', icon: 'pi pi-exclamation-triangle' },
    { status: 'More than 45 days', icon: 'pi pi-check' }
  ];

  private attachmentUrlCache = new Map<number, { url: string; expires: number }>();

  settlementAmount: number | null = null;

  constructor(
    private messageService: MessageService,
    private cd: ChangeDetectorRef,
    private selloutService: SelloutService,
    private settlementService: SettlementService,
    private route: ActivatedRoute,
    private router: Router,
    private workflowApprovalService: WorkflowApprovalService,
    private chatSessionService: ChatSessionService,
    private authorizationService: AuthorizationService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.currentUser = JSON.parse(sessionStorage.getItem('appUser') ?? 'null');

    let routeWithSessionId: ActivatedRoute | null = this.route;
    while (routeWithSessionId && !routeWithSessionId.snapshot.paramMap.has('sessionId')) {
      routeWithSessionId = routeWithSessionId.parent;
    }

    this.routeSub = (routeWithSessionId || this.route).paramMap.subscribe(params => {
      const sessionIdParam = params.get('sessionId');
      if (sessionIdParam) {
        this.currentSessionId = +sessionIdParam;
        this.sessionSub = this.chatSessionService.sessions$.subscribe(sessions => {
          this.currentSession = sessions.find(s => s.sessionId === this.currentSessionId);
        });
      }
    });

    let routeWithSelloutId: ActivatedRoute | null = this.route;
    while (routeWithSelloutId && !routeWithSelloutId.snapshot.paramMap.has('selloutId')) {
      routeWithSelloutId = routeWithSelloutId.parent;
    }

    this.routeSub = (routeWithSelloutId || this.route).paramMap.subscribe(params => {
      const selloutIdParam = params.get('selloutId');
      if (selloutIdParam) {
        this.selloutId = +selloutIdParam;
        this.loadSelloutData(this.selloutId);
      }
    });
  }

  loadSelloutData(selloutId: number): void {
    this.isLoading = true;
    forkJoin({
      header: this.selloutService.getSelloutHeaderById(selloutId),
      approvals: this.workflowApprovalService.getAllHistory(new HttpParams({
        fromObject: {
          resourceTypeId: 1,
          businessObjectId: selloutId
        }
      }))
    }).subscribe({
      next: ({ header, approvals }) => {
        this.selloutHeader = header;
        this.selloutHeader.workflowApprovals = approvals.content;
        this.setTimelineStage();
        this.checkAuthorization();

        if (this.selloutHeader?.settlementId) {
          this.settlementService.getSettlementById(this.selloutHeader.settlementId).subscribe({
            next: (settlement) => {
              this.settlementAmount = settlement.value ?? settlement.totalAmount ?? null;
              if (this.selloutHeader) {
                if (!this.selloutHeader.business && settlement.business) {
                  this.selloutHeader.business = settlement.business;
                }
                this.selloutHeader.settlementAmount = this.settlementAmount ?? undefined;
                if (this.settlementAmount != null && !this.isAmountWithinVariance) {
                  if (this.selloutHeader.overrideStatus === 'VARIANCE_OVERRIDE' && this.isOverrideEligible) {
                    this.selloutHeader.validationStatus = 'VARIANCE_OVERRIDE';
                  } else {
                    this.selloutHeader.validationStatus = 'NOT_MATCHED';
                  }
                }
              }
              this.isLoading = false;
              this.cd.detectChanges();
            },
            error: (err) => {
              console.error('Error loading settlement for sellout header', err);
              this.settlementAmount = this.selloutHeader?.settlementAmount ?? null;
              if (this.selloutHeader && this.settlementAmount != null && !this.isAmountWithinVariance) {
                if (this.selloutHeader.overrideStatus === 'VARIANCE_OVERRIDE' && this.isOverrideEligible) {
                  this.selloutHeader.validationStatus = 'VARIANCE_OVERRIDE';
                } else {
                  this.selloutHeader.validationStatus = 'NOT_MATCHED';
                }
              }
              this.isLoading = false;
              this.cd.detectChanges();
            }
          });
        } else {
          this.settlementAmount = this.selloutHeader?.settlementAmount ?? null;
          if (this.selloutHeader && this.settlementAmount != null && !this.isAmountWithinVariance) {
            if (this.selloutHeader.overrideStatus === 'VARIANCE_OVERRIDE' && this.isOverrideEligible) {
              this.selloutHeader.validationStatus = 'VARIANCE_OVERRIDE';
            } else {
              this.selloutHeader.validationStatus = 'NOT_MATCHED';
            }
          }
          this.isLoading = false;
          this.cd.detectChanges();
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.selloutHeader = null;
        this.messageService.add({ severity: 'error', summary: 'Load Failed', detail: `Could not load sellout ${selloutId}.` });
        console.error('Error loading sellout', err);
      }
    });
  }

  private setTimelineStage(): void {
    if (!this.selloutHeader?.selloutStartDate || !this.selloutHeader?.selloutEndDate) {
      this.currentTimelineStage = 'Not Started';
      return;
    }

    const today = new Date();
    const start = new Date(this.selloutHeader.selloutStartDate);
    const end = new Date(this.selloutHeader.selloutEndDate);
    const fortyFiveDaysAfterEnd = new Date(end);
    fortyFiveDaysAfterEnd.setDate(end.getDate() + 45);

    if (today < start) {
      this.currentTimelineStage = 'Not Started';
    } else if (today <= end) {
      this.currentTimelineStage = 'Under Execution';
    } else if (today <= fortyFiveDaysAfterEnd) {
      this.currentTimelineStage = 'Within 45 days';
    } else {
      this.currentTimelineStage = 'More than 45 days';
    }
  }

  private checkAuthorization(): void {
    this.canGenerateSettlement = this.authorizationService.hasPermission(this.currentUser, 'Settlement', ['create']);
    this.canUploadAttachment = this.authorizationService.hasPermission(this.currentUser, 'Promo Sellout Attachment', ['create']);
    this.canOverrideHeader = this.authorizationService.hasPermission(this.currentUser, 'Sellout', ['update']);
  }

  getStatusSeverity(): string {
    switch (this.selloutHeader?.workflowStatus?.statusName) {
      case 'Settled':
      case 'Completed':
        return 'success';
      case 'Changed':
        return 'warn';
      case 'Cancelled':
        return 'danger';
      default:
        return 'info';
    }
  }

  getValidationTagSeverity(status?: string): string {
    switch (status) {
      case 'VALIDATED':
        return 'success';
      case 'VARIANCE_FOUND':
        return 'warn';
      case 'MAPPING_ISSUE':
        return 'danger';
      case 'NOT_MATCHED':
        return 'danger';
      case 'VARIANCE_OVERRIDE':
        return 'contrast';
      default:
        return 'info';
    }
  }

  getVarianceClass(value?: number | null): string {
    if (value == null || value === 0) {
      return 'variance-neutral';
    }
    return value > 0 ? 'variance-positive' : 'variance-negative';
  }

  formatVariance(value?: number | null): string {
    if (value == null) {
      return '-';
    }
    if (value === 0) {
      return 'No Variance';
    }
    return `${value > 0 ? '+' : ''}${value}`;
  }

  getQuantityValidationLabel(detail: SelloutDetail): string {
    if (!detail.quantityValidationMessage) {
      return 'N/A';
    }
    if (detail.quantityValidationMatched === true) {
      return detail.quantityValidationQuantity != null ? 'CHECKED' : 'INFO';
    }
    return 'NOT_FOUND';
  }

  getQuantityValidationSeverity(detail: SelloutDetail): string {
    if (!detail.quantityValidationMessage) {
      return 'secondary';
    }
    return detail.quantityValidationMatched === true ? 'info' : 'warn';
  }

  formatFileSize(bytes: number | undefined, decimals = 2): string {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  onStatusChange(status: WorkflowApproval): void {
    if (this.selloutHeader) {
      this.selloutHeader.workflowStatus = status.workflowStatus;
    }
  }

  generateNewSettlement(): void {
    if (this.selloutId && this.currentSessionId !== null) {
      this.router.navigate(
        ['/ai-studio/session', this.currentSessionId, { outlets: { canvasOutlet: ['settlement', 'add'] } }],
        { queryParams: { selloutId: this.selloutId } }
      );
    }
  }

  openSession(): void {
    if (this.selloutHeader?.linkedSessionId) {
      this.router.navigate([
        '/ai-studio/session',
        this.selloutHeader.linkedSessionId,
        { outlets: { canvasOutlet: ['sellout', 'view', this.selloutHeader.selloutId] } }
      ]);
    }
  }

  exportDetailsToExcel(): void {
    if (!this.selloutHeader) {
      return;
    }

    const details = this.selloutHeader.details ?? [];
    const dataToExport = details.map(detail => this.mapDetailToExportRow(detail));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);

    // Inject live formulas and precalculated values for ENTITLED AMOUNT (Col V) and TOTAL VARIANCE (Col X)
    details.forEach((detail, idx) => {
      const rowNum = idx + 2; // Row 1 is header
      const hasApprovedComp = detail.approvedCompensationPerUnit != null && !Number.isNaN(Number(detail.approvedCompensationPerUnit));
      const hasQty = detail.quantity != null && !Number.isNaN(Number(detail.quantity));

      if (hasApprovedComp && hasQty) {
        const entitledVal = Math.round(Number(detail.approvedCompensationPerUnit) * Number(detail.quantity) * 100) / 100;
        ws[`V${rowNum}`] = {
          t: 'n',
          v: entitledVal,
          f: `S${rowNum}*U${rowNum}`
        };

        if (detail.totalAmount != null && !Number.isNaN(Number(detail.totalAmount))) {
          const varianceVal = Math.round((Number(detail.totalAmount) - entitledVal) * 100) / 100;
          ws[`X${rowNum}`] = {
            t: 'n',
            v: varianceVal,
            f: `W${rowNum}-V${rowNum}`
          };
        }
      }
    });

    // Set readable column widths
    ws['!cols'] = [
      { wch: 14 }, // A: SELLOUT ID
      { wch: 24 }, // B: MATCHED PROMO PLAN ID
      { wch: 28 }, // C: MATCHED PROMO PLAN LINE ID
      { wch: 18 }, // D: CUSTOMER NUMBER
      { wch: 30 }, // E: CUSTOMER NAME
      { wch: 12 }, // F: CHANNEL
      { wch: 22 }, // G: CUSTOMER ITEM NUMBER
      { wch: 28 }, // H: UPLOADED CUSTOMER ITEM NUMBER
      { wch: 35 }, // I: CUSTOMER ITEM DESCRIPTION
      { wch: 28 }, // J: MATCHED CUSTOMER ITEM NUMBER
      { wch: 20 }, // K: TAMER SUB-BRAND
      { wch: 24 }, // L: PROMO PLAN BATCH NUMBER
      { wch: 18 }, // M: SELLOUT START DATE
      { wch: 18 }, // N: SELLOUT END DATE
      { wch: 18 }, // O: PROMO START DATE
      { wch: 18 }, // P: PROMO END DATE
      { wch: 45 }, // Q: MONTH YEAR (To be related to the Sellout end date)
      { wch: 24 }, // R: COMPENSATION PER UNIT
      { wch: 30 }, // S: APPROVED COMPENSATION PER UNIT
      { wch: 30 }, // T: COMPENSATION PER UNIT VARIANCE
      { wch: 14 }, // U: QUANTITY
      { wch: 18 }, // V: ENTITLED AMOUNT
      { wch: 16 }, // W: TOTAL AMOUNT
      { wch: 16 }, // X: TOTAL VARIANCE
      { wch: 20 }, // Y: PROMO PLAN MATCHED
      { wch: 20 }, // Z: VALIDATION STATUS
      { wch: 50 }, // AA: VALIDATION MESSAGE
      { wch: 20 }, // AB: NOTE
      { wch: 24 }, // AC: HEADER OVERRIDE STATUS
      { wch: 30 }, // AD: HEADER OVERRIDE JUSTIFICATION
      { wch: 25 }, // AE: STATUS
    ];

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sellout Details');
    XLSX.writeFile(wb, `Sellout_${this.selloutHeader.selloutId}.xlsx`);
  }

  onFileSelect(event: any): void {
    if (!this.selloutHeader) return;
    if (!this.selloutHeader.attachments) {
      this.selloutHeader.attachments = [];
    }

    for (const file of event.files) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.selloutHeader!.attachments.push({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          fileContent: e.target.result
        });
        this.attachmentsChanged = true;
        this.cd.detectChanges();
      };
      reader.readAsDataURL(file);
    }
  }

  saveAttachments(): void {
    if (!this.selloutHeader?.selloutId || !this.selloutHeader.attachments) {
      return;
    }

    const newAttachments = this.selloutHeader.attachments.filter(att => att.fileContent);
    if (newAttachments.length === 0) {
      this.attachmentsChanged = false;
      return;
    }

    this.isLoading = true;
    this.selloutService.uploadAttachments(this.selloutHeader.selloutId, newAttachments).subscribe({
      next: (savedAttachments) => {
        this.isLoading = false;
        this.attachmentsChanged = false;
        this.selloutHeader!.attachments = this.selloutHeader!.attachments.filter(att => !att.fileContent);
        this.selloutHeader!.attachments.push(...savedAttachments);
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save attachments.' });
        console.error('Error saving attachments', err);
      }
    });
  }

  viewAttachment(attachment: SelloutAttachment): void {
    this.executeAttachmentAction(attachment, 'view');
  }

  downloadAttachment(attachment: SelloutAttachment): void {
    this.executeAttachmentAction(attachment, 'download');
  }

  emailAttachment(attachment: SelloutAttachment): void {
    this.executeAttachmentAction(attachment, 'email');
  }

  private executeAttachmentAction(attachment: SelloutAttachment, actionType: 'view' | 'download' | 'email'): void {
    if (actionType === 'download' && attachment.fileContent) {
      this.downloadLocalAttachment(attachment);
      return;
    }

    if (!this.selloutId || !attachment.attachmentId) {
      return;
    }

    const cachedUrl = this.attachmentUrlCache.get(attachment.attachmentId);
    if (cachedUrl && cachedUrl.expires > Date.now()) {
      this.performActionWithUrl(actionType, attachment, cachedUrl.url);
      return;
    }

    this.selloutService.getAttachmentViewUrl(this.selloutId, attachment.attachmentId).subscribe({
      next: (response) => {
        const expirationTime = Date.now() + 5 * 60 * 1000;
        this.attachmentUrlCache.set(attachment.attachmentId!, { url: response.url, expires: expirationTime });
        this.performActionWithUrl(actionType, attachment, response.url);
      }
    });
  }

  private performActionWithUrl(actionType: 'view' | 'download' | 'email', attachment: SelloutAttachment, url: string): void {
    switch (actionType) {
      case 'view':
        if ((attachment.fileName || '').toLowerCase().endsWith('.pdf')) {
          this.pdfSrc = url;
          this.pdfViewerVisible = true;
        } else {
          window.open(url, '_blank');
        }
        break;
      case 'download': {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', attachment.fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        break;
      }
      case 'email': {
        const subject = `Attachment: ${attachment.fileName} from Sellout #${this.selloutHeader?.selloutId}`;
        const body = `Please find the attached file: ${attachment.fileName}\n\n${url}`;
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        break;
      }
    }
  }

  private downloadLocalAttachment(attachment: SelloutAttachment): void {
    if (!attachment.fileContent) return;
    const base64Response = attachment.fileContent.split(',')[1];
    const byteCharacters = atob(base64Response);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: attachment.fileType });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = attachment.fileName;
    link.click();
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.sessionSub?.unsubscribe();
  }

  get detailRows(): SelloutDetail[] {
    return this.selloutHeader?.details ?? [];
  }

  get totalQuantity(): number {
    return this.detailRows.reduce((sum, detail) => sum + (detail.quantity ?? 0), 0);
  }

  get totalAmount(): number {
    return this.detailRows.reduce((sum, detail) => sum + (detail.totalAmount ?? 0), 0);
  }

  get promoPlanIdsLabel(): string {
    const promoPlanIds = [...new Set(
      this.detailRows
        .map(detail => detail.matchedPromoPlanId)
        .filter((promoPlanId): promoPlanId is number => promoPlanId != null)
    )];

    return promoPlanIds.length > 0 ? promoPlanIds.join(', ') : '-';
  }

  getCustomerItemLabel(detail: SelloutDetail): string {
    const itemNumber = detail.customerItem?.customerItemNumber;
    const description = detail.customerItem?.customerItemDescription;

    if (itemNumber && description) {
      return `${itemNumber} - ${description}`;
    }
    return itemNumber || description || detail.uploadedCustomerItemNumber || '-';
  }

  get isAmountWithinVariance(): boolean {
    if (this.settlementAmount == null || this.settlementAmount <= 0) {
      return true;
    }
    const minAllowed = this.settlementAmount * 0.95;
    const maxAllowed = this.settlementAmount * 1.05;
    return this.totalAmount >= minAllowed && this.totalAmount <= maxAllowed;
  }

  get isOverrideEligible(): boolean {
    if (!this.selloutHeader) {
      return false;
    }

    const details = this.detailRows;
    if (details.length === 0) {
      return false;
    }

    let hasOverrideableIssue = !this.isAmountWithinVariance;

    for (const detail of details) {
      if (!detail.customerItem) {
        return false;
      }
      if (detail.validationStatus === 'MAPPING_ISSUE') {
        return false;
      }
      if (detail.validationStatus === 'NOT_MATCHED') {
        if (detail.quantityValidationMatched === false) {
          hasOverrideableIssue = true;
        } else {
          return false;
        }
      } else if (detail.validationStatus === 'VARIANCE_FOUND') {
        hasOverrideableIssue = true;
      }
    }

    return hasOverrideableIssue;
  }

  get canShowHeaderOverrideAction(): boolean {
    return !!this.selloutHeader
      && this.canOverrideHeader
      && this.selloutHeader.overrideStatus?.toUpperCase() !== 'VARIANCE_OVERRIDE'
      && this.isOverrideEligible;
  }

  openOverrideDialog(): void {
    this.overrideJustification = this.selloutHeader?.overrideJustification ?? '';
    this.overrideDialogVisible = true;
  }

  applyHeaderOverride(): void {
    if (!this.selloutHeader?.selloutId || !this.overrideJustification.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Justification Required', detail: 'Please enter override justification.' });
      return;
    }

    this.isApplyingOverride = true;
    this.selloutService.applyHeaderOverride(this.selloutHeader.selloutId, this.overrideJustification.trim()).subscribe({
      next: (updatedHeader) => {
        this.selloutHeader = {
          ...updatedHeader,
          workflowApprovals: this.selloutHeader?.workflowApprovals ?? updatedHeader.workflowApprovals
        };
        this.overrideDialogVisible = false;
        this.isApplyingOverride = false;
        this.messageService.add({ severity: 'success', summary: 'Override Applied', detail: 'Header override was applied successfully.' });
      },
      error: (err) => {
        this.isApplyingOverride = false;
        this.messageService.add({ severity: 'error', summary: 'Override Failed', detail: 'Could not apply header override.' });
        console.error('Error applying sellout header override', err);
      }
    });
  }

  private mapDetailToExportRow(detail: SelloutDetail): Record<string, string | number | boolean | undefined> {
    const hasApprovedComp = detail.approvedCompensationPerUnit != null && !Number.isNaN(Number(detail.approvedCompensationPerUnit));
    const hasQty = detail.quantity != null && !Number.isNaN(Number(detail.quantity));
    const entitledAmount: number | string = hasApprovedComp && hasQty
      ? Math.round(Number(detail.approvedCompensationPerUnit) * Number(detail.quantity) * 100) / 100
      : '-';

    const hasTotalAmount = detail.totalAmount != null && !Number.isNaN(Number(detail.totalAmount));
    let totalVariance: number | string = '-';
    if (typeof entitledAmount === 'number' && hasTotalAmount) {
      totalVariance = Math.round((Number(detail.totalAmount) - entitledAmount) * 100) / 100;
    } else if (hasTotalAmount && (detail.validationStatus === 'MAPPING_ISSUE' || detail.validationStatus === 'NOT_MATCHED')) {
      totalVariance = Number(detail.totalAmount);
    }

    const selloutEndDate = detail.selloutEndDate ?? this.selloutHeader?.selloutEndDate;

    return {
      'SELLOUT ID': this.selloutHeader?.selloutId,
      'MATCHED PROMO PLAN ID': detail.matchedPromoPlanId,
      'MATCHED PROMO PLAN LINE ID': detail.matchedPromoPlanLineId,
      'CUSTOMER NUMBER': this.selloutHeader?.customer?.customerNumber,
      'CUSTOMER NAME': this.selloutHeader?.customer?.customerName,
      'CHANNEL': this.selloutHeader?.channel,
      'CUSTOMER ITEM NUMBER': detail.customerItem?.customerItemNumber,
      'UPLOADED CUSTOMER ITEM NUMBER': detail.uploadedCustomerItemNumber,
      'CUSTOMER ITEM DESCRIPTION': detail.customerItem?.customerItemDescription,
      'MATCHED CUSTOMER ITEM NUMBER': detail.matchedCustomerItemNumber,
      'TAMER SUB-BRAND': this.getDetailSubBrand(detail),
      'PROMO PLAN BATCH NUMBER': detail.promoPlanBatchNumber,
      'SELLOUT START DATE': this.formatExportDateDDMMYYYY(detail.selloutStartDate ?? this.selloutHeader?.selloutStartDate),
      'SELLOUT END DATE': this.formatExportDateDDMMYYYY(selloutEndDate),
      'PROMO START DATE': this.formatExportDateDDMMYYYY(detail.promoStartDate),
      'PROMO END DATE': this.formatExportDateDDMMYYYY(detail.promoEndDate),
      'MONTH YEAR (To be related to the Sellout end date)': this.formatExportMonthYear(selloutEndDate),
      'COMPENSATION PER UNIT': detail.compensationPerUnit,
      'APPROVED COMPENSATION PER UNIT': detail.approvedCompensationPerUnit,
      'COMPENSATION PER UNIT VARIANCE': detail.compensationPerUnitVariance,
      'QUANTITY': detail.quantity,
      'ENTITLED AMOUNT': entitledAmount,
      'TOTAL AMOUNT': detail.totalAmount,
      'TOTAL VARIANCE': totalVariance,
      'PROMO PLAN MATCHED': detail.promoPlanLineMatched,
      'VALIDATION STATUS': detail.validationStatus,
      'VALIDATION MESSAGE': detail.validationMessage,
      'NOTE': detail.note,
      'HEADER OVERRIDE STATUS': this.selloutHeader?.overrideStatus,
      'HEADER OVERRIDE JUSTIFICATION': this.selloutHeader?.overrideJustification,
      'STATUS': this.selloutHeader?.workflowStatus?.statusName
    };
  }

  private getDetailSubBrand(detail: SelloutDetail): string {
    return (
      detail.customerItem?.subBrand?.subBrandName ||
      detail.customerItem?.tamerItem?.subBrand?.subBrandName ||
      '-'
    );
  }

  private parseDateComponents(value?: string | Date | number | any[] | null): { year: number; month: number; day: number } | null {
    if (!value) {
      return null;
    }

    if (Array.isArray(value) && value.length >= 3) {
      return {
        year: Number(value[0]),
        month: Number(value[1]),
        day: Number(value[2])
      };
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      const commaMatch = trimmed.match(/^(\d{4}),\s*(\d{1,2}),\s*(\d{1,2})$/);
      if (commaMatch) {
        return {
          year: parseInt(commaMatch[1], 10),
          month: parseInt(commaMatch[2], 10),
          day: parseInt(commaMatch[3], 10)
        };
      }

      const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (isoMatch) {
        return {
          year: parseInt(isoMatch[1], 10),
          month: parseInt(isoMatch[2], 10),
          day: parseInt(isoMatch[3], 10)
        };
      }
    }

    const d = new Date(value as any);
    if (Number.isNaN(d.getTime())) {
      return null;
    }

    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate()
    };
  }

  private formatExportDateDDMMYYYY(value?: string | Date | number | any[] | null): string {
    const comp = this.parseDateComponents(value);
    if (!comp) {
      return '-';
    }

    const day = String(comp.day).padStart(2, '0');
    const month = String(comp.month).padStart(2, '0');
    return `${day}/${month}/${comp.year}`;
  }

  private formatExportMonthYear(value?: string | Date | number | any[] | null): string {
    const comp = this.parseDateComponents(value);
    if (!comp) {
      return '-';
    }

    const month = String(comp.month).padStart(2, '0');
    const shortYear = String(comp.year).slice(-2);
    return `${month}/${shortYear}`;
  }
}
